/**
 * Benefits — /benefits
 * ---------------------------------------------------------------------------
 * Consolidated explainer page for hotel partner benefits. Replaces the prior
 * per-program detail pages (e.g. /four-seasons-preferred-partners), which now
 * 301 to this page (see routes/web.php).
 *
 * Sections (all dark teal):
 *   1. Hero — heading + Dek
 *   2. What are benefits? — short editorial explainer
 *   3. Typical benefits — 6-item editorial list
 *   4. Brand logos grid — 18 program partners (matches "Unlocked with [Name]"
 *      pattern from agent profile pages)
 *   5. Closing CTA — Speak with an Advisor
 */

import MetaHead from '@/components/meta-head';
import { useInquiryModal } from '@/components/inquiry/InquiryModal';
import PublicLayout from '@/layouts/public-layout';
import { logoPictureSources, partnerLogoIntrinsicSize } from '@/lib/responsive-image';
import { cn } from '@/lib/utils';
import { motion, useReducedMotion } from 'framer-motion';

/* ------------------------------------------------------------------ */
/*  Animation                                                          */
/* ------------------------------------------------------------------ */

const EASE: [number, number, number, number] = [0.22, 1, 0.36, 1];

const fadeUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: EASE } },
};

const stagger = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.08, delayChildren: 0.15 } },
};

/* ------------------------------------------------------------------ */
/*  Typical benefits — customer-friendly phrasing for the standard     */
/*  upper-tier hotel-program inclusions                                */
/* ------------------------------------------------------------------ */

const TYPICAL_BENEFITS = [
    {
        title: 'Daily breakfast for two',
        body: 'Usually delivered as a daily credit — typically around $80 — that covers most à la carte breakfasts at the property.',
    },
    {
        title: 'Room upgrade on arrival',
        body: 'One category up, subject to availability at check-in. Sometimes two when the property has space.',
    },
    {
        title: 'Early check-in & late checkout',
        body: 'Subject to availability. We pre-confirm both with the hotel before you arrive so the day reads as smoothly as possible.',
    },
    {
        title: 'Hotel credit',
        body: 'Typically $100 toward dining, spa, or property charges. Some programs offer more; the credit is per stay, not per night.',
    },
    {
        title: 'Complimentary high-speed Wi-Fi',
        body: 'Premium tier where the property tiers it. Standard inclusion across nearly every program.',
    },
    {
        title: 'VIP welcome amenity',
        body: 'Often a glass of champagne, a fruit plate, or a handwritten note. The hotel knows you’re coming.',
    },
];

/* ------------------------------------------------------------------ */
/*  Brand logos — 18 partner programs                                  */
/* ------------------------------------------------------------------ */

/* ------------------------------------------------------------------ */
/*  Editorial photos — full-bleed property shots interleaved between    */
/*  sections. Caption matches the article masthead style: small caps    */
/*  tracked uppercase at /35 ivory, centered below the image.           */
/* ------------------------------------------------------------------ */

const EDITORIAL_PHOTOS = {
    firenze: {
        src: 'https://cdnweb.luxrallytravel.com/design-studio/images/2026/04/21cb679f-296b-40d0-ba95-17d82a6958e0.jpg',
        alt: 'Four Seasons Firenze, Florence',
        caption: 'Four Seasons Hotel Firenze · Florence',
    },
    jimbaran: {
        src: 'https://cdnweb.luxrallytravel.com/2026/03/1b89d98a-90d3-4a9a-9014-8508e797e792.png',
        alt: 'Belmond Jimbaran Puri, Bali',
        caption: 'Belmond Jimbaran Puri · Bali',
    },
    paris: {
        src: 'https://cdnweb.luxrallytravel.com/design-studio/images/2026/04/d5db26ea-3f58-4e71-8faa-04bc34fd499b.png',
        alt: 'Shangri-La, Paris',
        caption: 'Shangri-La · Paris',
    },
} as const;

interface EditorialPhotoProps {
    photo: (typeof EDITORIAL_PHOTOS)[keyof typeof EDITORIAL_PHOTOS];
}

function EditorialPhoto({ photo }: EditorialPhotoProps) {
    return (
        <figure className="w-full">
            <img
                src={photo.src}
                alt={photo.alt}
                loading="lazy"
                decoding="async"
                className="aspect-[16/9] w-full object-cover"
            />
            <figcaption className="mx-auto mt-4 max-w-3xl px-6 text-center text-[11px] tracking-[0.2em] uppercase text-[#F5F5F5]/35">
                {photo.caption}
            </figcaption>
        </figure>
    );
}

const BRAND_LOGOS = [
    { name: 'Four Seasons Preferred Partners', logo: 'https://cdnweb.luxrallytravel.com/images/logos/v3/four-seasons-preferred-partners-trimmed.png' },
    { name: 'Hyatt Privé', logo: 'https://cdnweb.luxrallytravel.com/images/logos/v3/hyatt-prive-trimmed.png' },
    { name: 'Mandarin Oriental Fan Club', logo: 'https://cdnweb.luxrallytravel.com/images/logos/v3/mandarin-oriental-fan-club-trimmed.png' },
    { name: 'Rosewood Elite', logo: 'https://cdnweb.luxrallytravel.com/images/logos/v3/rosewood-elite-trimmed.png' },
    { name: 'Peninsula PenClub', logo: 'https://cdnweb.luxrallytravel.com/images/logos/v3/peninsula-penclub-trimmed.png' },
    { name: 'Belmond Bellini Club', logo: 'https://cdnweb.luxrallytravel.com/images/logos/v3/belmond-bellini-club-trimmed.png' },
    { name: 'Shangri-La Luxury Circle', logo: 'https://cdnweb.luxrallytravel.com/images/logos/v3/shangri-la-luxury-circle-trimmed.png' },
    { name: 'Hilton For Luxury', logo: 'https://cdnweb.luxrallytravel.com/images/logos/v3/hilton-for-luxury-trimmed.png' },
    { name: 'Jumeirah Passport to Luxury', logo: 'https://cdnweb.luxrallytravel.com/images/logos/v3/jumeirah-passport-to-luxury-trimmed.png' },
    { name: 'Couture by Langham', logo: 'https://cdnweb.luxrallytravel.com/images/logos/v3/couture-by-langham-trimmed.png' },
    { name: 'IHG Destined', logo: 'https://cdnweb.luxrallytravel.com/images/logos/v3/ihg-destined-trimmed.png' },
    { name: 'Accor Preferred HERA', logo: 'https://cdnweb.luxrallytravel.com/images/logos/v3/accor-preferred-hera-trimmed.png' },
    { name: 'Platinum Partner', logo: 'https://cdnweb.luxrallytravel.com/images/logos/v3/platinum-partner-trimmed.png' },
    { name: 'Select by Internova', logo: 'https://cdnweb.luxrallytravel.com/images/logos/v3/select-by-internova-trimmed.png' },
    { name: 'ABC•CCRA Premier Hotel Program', logo: 'https://cdnweb.luxrallytravel.com/images/logos/v3/abcccra-premier-hotel-program-trimmed.png' },
    { name: 'Travel Leaders Network', logo: 'https://cdnweb.luxrallytravel.com/images/logos/v3/travel-leaders-network-trimmed.png' },
    { name: 'Noble House VIP Select', logo: 'https://cdnweb.luxrallytravel.com/images/logos/v3/noble-house-vip-select-trimmed.png' },
    { name: 'Trump Preferred Partner', logo: 'https://cdnweb.luxrallytravel.com/images/logos/v3/trump-preferred-partner-trimmed.png' },
];

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/*                                                                     */
/*  BenefitsContent must live inside PublicLayout (which mounts the    */
/*  InquiryModalProvider). If useInquiryModal() is called from the     */
/*  top-level Benefits() component, the context isn't established yet  */
/*  and the CTA silently no-ops.                                       */
/* ------------------------------------------------------------------ */

function BenefitsContent() {
    const shouldReduceMotion = useReducedMotion();
    const { openInquiry } = useInquiryModal();

    const mp = (props: Record<string, unknown>) => (shouldReduceMotion ? {} : props);

    const handleInquiry = () => {
        openInquiry({
            source: 'benefits_page',
            page_url: typeof window !== 'undefined' ? window.location.href : '',
        });
    };

    return (
        <div className="bg-[#0e2727]">
                {/* ============================================= */}
                {/* HERO                                            */}
                {/* ============================================= */}
                <section className="lux-space-lg nav-px w-full pt-32 md:pt-40">
                    <div className="mx-auto max-w-3xl text-center">
                        <motion.div
                            {...mp({
                                initial: { opacity: 0, y: 12 },
                                whileInView: { opacity: 1, y: 0 },
                                viewport: { once: true },
                                transition: { duration: 0.7, ease: EASE },
                            })}
                        >
                            <p className="lux-caps lux-caps-sm mb-4 text-[#F5F5F5]/60">Hotel Benefits</p>
                            <h1 className="lux-didot lux-didot-xl text-balance text-[#F5F5F5]">Hotel benefits, explained.</h1>
                            <p className="lux-body lux-body-md mx-auto mt-6 max-w-xl leading-relaxed text-[#F5F5F5]/75">
                                Hotel partner programs are one of the more talked-about parts of how travel agencies work — and one of the least understood. Here&rsquo;s what these programs actually are, what they typically include, and where they fit in.
                            </p>
                        </motion.div>
                    </div>
                </section>

                {/* ============================================= */}
                {/* EDITORIAL PHOTO — Four Seasons Firenze         */}
                {/* ============================================= */}
                <EditorialPhoto photo={EDITORIAL_PHOTOS.firenze} />

                {/* ============================================= */}
                {/* WHAT ARE BENEFITS — explainer                  */}
                {/* ============================================= */}
                <section className="lux-space-lg nav-px w-full">
                    <div className="mx-auto max-w-2xl">
                        <motion.div
                            {...mp({
                                initial: { opacity: 0, y: 16 },
                                whileInView: { opacity: 1, y: 0 },
                                viewport: { once: true, margin: '-60px' },
                                transition: { duration: 0.7, ease: EASE },
                            })}
                            className="text-center"
                        >
                            <p className="lux-caps lux-caps-sm mb-3 text-[#F5F5F5]/60">What are benefits?</p>
                            <h2 className="lux-didot lux-didot-lg text-balance text-[#F5F5F5]">One tool, used when it makes sense.</h2>
                            <div className="mx-auto mt-6 space-y-5 text-left">
                                <p className="lux-body lux-body-md leading-relaxed text-[#F5F5F5]/75">
                                    Hotels distribute a portion of their rates through trusted partner programs — networks like Four Seasons Preferred, Mandarin Oriental Fan Club, Hyatt Privé. When a reservation comes in through one of these programs, the hotel layers extras onto the stay: breakfast, an upgrade, a property credit, recognition at check-in.
                                </p>
                                <p className="lux-body lux-body-md leading-relaxed text-[#F5F5F5]/75">
                                    We participate in nearly every major partner program — and when a property isn&rsquo;t in one we&rsquo;re part of, we can often arrange comparable extras through the property directly. Where a benefit rate is competitive with the alternatives, we book you through it and the extras come along. Where a wholesale or direct rate is meaningfully cheaper — sometimes by hundreds, sometimes by thousands — we lay both options out and let you pick: the lower rate without the breakfast, or the higher rate with it.
                                </p>
                                <p className="lux-body lux-body-md leading-relaxed text-[#F5F5F5]/75">
                                    Either way, your reservation arrives with our name attached, and that carries weight on its own. Hotels know us through our in-house concierge team, the volume we send to their brand, and the relationships our advisors maintain with property management. The partner programs are one channel of pull. They&rsquo;re not the whole story.
                                </p>
                            </div>
                        </motion.div>
                    </div>
                </section>

                {/* ============================================= */}
                {/* EDITORIAL PHOTO — Belmond Jimbaran Puri Bali   */}
                {/* ============================================= */}
                <EditorialPhoto photo={EDITORIAL_PHOTOS.jimbaran} />

                {/* ============================================= */}
                {/* TYPICAL BENEFITS — editorial list              */}
                {/* ============================================= */}
                <section className="lux-space-lg nav-px w-full">
                    <div className="mx-auto max-w-2xl md:max-w-3xl lg:max-w-5xl xl:max-w-6xl">
                        <motion.div
                            {...mp({
                                initial: { opacity: 0, y: 12 },
                                whileInView: { opacity: 1, y: 0 },
                                viewport: { once: true },
                                transition: { duration: 0.7, ease: EASE },
                            })}
                            className="mx-auto mb-16 max-w-3xl text-center md:mb-20"
                        >
                            <p className="lux-caps lux-caps-sm mb-3 text-[#F5F5F5]/60">What you typically get</p>
                            <h2 className="lux-didot lux-didot-lg text-balance text-[#F5F5F5]">The standard inclusions.</h2>
                        </motion.div>

                        <motion.div
                            {...mp({
                                variants: stagger,
                                initial: 'hidden',
                                whileInView: 'visible',
                                viewport: { once: true, margin: '-60px' },
                            })}
                            className="grid grid-cols-1 gap-x-12 gap-y-14 sm:grid-cols-2 lg:grid-cols-3 lg:gap-x-16 lg:gap-y-16"
                        >
                            {TYPICAL_BENEFITS.map((item) => (
                                <motion.div
                                    key={item.title}
                                    {...mp({ variants: fadeUp })}
                                    className="relative flex flex-col items-center text-center"
                                >
                                    <h3 className="lux-didot lux-didot-md text-balance text-[#F5F5F5]">{item.title}</h3>
                                    <p className="lux-body lux-body-sm mt-3 max-w-[34ch] leading-relaxed text-[#F5F5F5]/70">{item.body}</p>
                                </motion.div>
                            ))}
                        </motion.div>

                        <motion.p
                            {...mp({
                                initial: { opacity: 0, y: 12 },
                                whileInView: { opacity: 1, y: 0 },
                                viewport: { once: true },
                                transition: { duration: 0.6, delay: 0.1, ease: EASE },
                            })}
                            className="lux-body lux-body-sm mx-auto mt-16 max-w-xl text-center text-[#F5F5F5]/50"
                        >
                            Specific inclusions vary by program and property — and benefits only apply when the booking is made through a partner-program rate. Your advisor will tell you when that&rsquo;s the better play and when a different rate makes more sense.
                        </motion.p>
                    </div>
                </section>

                {/* ============================================= */}
                {/* EDITORIAL PHOTO — Shangri-La Paris             */}
                {/* ============================================= */}
                <EditorialPhoto photo={EDITORIAL_PHOTOS.paris} />

                {/* ============================================= */}
                {/* BRAND LOGOS GRID — 18 partner programs         */}
                {/* ============================================= */}
                <section className="lux-space-lg nav-px w-full">
                    <div className="mx-auto max-w-[1600px]">
                        <motion.div
                            {...mp({
                                initial: { opacity: 0, y: 12 },
                                whileInView: { opacity: 1, y: 0 },
                                viewport: { once: true },
                                transition: { duration: 0.7, ease: EASE },
                            })}
                            className="mb-16 flex flex-col items-center text-center md:mb-20"
                        >
                            <p className="lux-caps lux-caps-sm mb-3 text-[#F5F5F5]/60">Our partner programs</p>
                            <h2 className="lux-didot lux-didot-lg text-balance text-[#F5F5F5]">A few of the programs.</h2>
                            <p className="lux-body lux-body-md mx-auto mt-5 max-w-xl leading-relaxed text-[#F5F5F5]/70">
                                We work with nearly every major hotel partner program. These are some of the ones clients ask about most often.
                            </p>
                        </motion.div>

                        <motion.div
                            {...mp({
                                variants: stagger,
                                initial: 'hidden',
                                whileInView: 'visible',
                                viewport: { once: true, margin: '-40px' },
                            })}
                            className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6"
                        >
                            {BRAND_LOGOS.map((brand) => {
                                const sources = logoPictureSources(brand.logo);
                                const { width, height } = partnerLogoIntrinsicSize(brand.logo);
                                const imgClass = cn(
                                    'w-auto object-contain opacity-50 brightness-0 invert',
                                    brand.name.includes('Four Seasons') ? 'h-16 -translate-y-[33px]' : 'h-8',
                                );
                                const imgEl = (
                                    <img src={brand.logo} alt={brand.name} loading="lazy" decoding="async" className={imgClass} draggable={false} width={width} height={height} />
                                );
                                return (
                                    <motion.div key={brand.name} {...mp({ variants: fadeUp })}>
                                        <div className="flex items-center justify-center rounded-sm px-4 py-5">
                                            {sources ? (
                                                <picture>
                                                    <source type="image/avif" srcSet={sources.avif} />
                                                    <source type="image/webp" srcSet={sources.webp} />
                                                    {imgEl}
                                                </picture>
                                            ) : (
                                                imgEl
                                            )}
                                        </div>
                                    </motion.div>
                                );
                            })}
                        </motion.div>
                    </div>
                </section>

                {/* ============================================= */}
                {/* CLOSING CTA                                    */}
                {/* ============================================= */}
                <motion.section
                    className="lux-space-lg nav-px w-full"
                    {...mp({
                        initial: 'hidden',
                        whileInView: 'visible',
                        viewport: { once: true, amount: 0.3 },
                        variants: fadeUp,
                    })}
                >
                    <div className="mx-auto max-w-2xl text-center">
                        <h2 className="lux-didot lux-didot-xl text-balance text-[#F5F5F5]">Ready to plan a stay?</h2>
                        <p className="lux-body lux-body-lg mt-5 text-[#F5F5F5]/60">
                            We&rsquo;ll match you with an advisor who knows the property and the program.
                        </p>
                        <button
                            type="button"
                            onClick={handleInquiry}
                            className="lux-caps lux-caps-sm mt-10 inline-flex cursor-pointer items-center justify-center rounded-sm border border-[#F5F5F5]/30 bg-[#081c1c] px-4 py-2.5 text-[#F5F5F5] transition-colors duration-300 hover:border-[#F5F5F5]/50 hover:bg-[#0a2020] sm:py-1"
                        >
                            Speak with an Advisor
                        </button>
                    </div>
                </motion.section>
            </div>
    );
}

export default function Benefits() {
    return (
        <PublicLayout
            headerConfig={{
                position: 'fixed',
                background: 'transparent',
                textColor: 'light',
                scrollBehavior: 'always',
            }}
        >
            <MetaHead
                title="Hotel Benefits, Explained | LuxRally Travel"
                description="An explainer on hotel partner programs — what they are, what they typically include, and the brands we're enrolled in. Benefits are one tool we use when they make sense, not the reason to book."
            />
            <BenefitsContent />
        </PublicLayout>
    );
}
